/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import React, { FunctionComponent, useEffect, useRef, useState } from 'react';

import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';
import { ActionKernelType, Detail, PreviewKernel } from 'client-code/actions';
import { SeasonsData } from 'client-code/episode-guide';
import { Credit } from 'client-code/person-content-service';

import viewAllImage from '../../../assets/images/hydra_icon_view_all_list.png';
import { APPLICATION_FEATURE_AREA, TIVO_SERVICES } from '../../../config';
import { BrandingUiInfoKeys } from '../../../core/entities';
import { ImageAssetsService } from '../../../core/infrastructure';
import { NdvrInteractor, ServicesClientImpl, VodInteractor } from '../../../core/interactors';
import { Locale, LocaleKeys } from '../../../core/locale';
import { Log } from '../../../core/logging';
import { ScreenController } from '../../../core/navigation/ScreenController';
import { SharedPrefConstants, SharedPreferences } from '../../../core/storage';
import { RootState, useTivoDispatch, useTivoSelector } from '../../../core/tivo/tivo-redux-wrapper';
import { TPreviewKernel, TPreviewKernelType } from '../../../core/types';
import { ImageUrlGenerator } from '../../../core/utils';
import { setHorizontalOptionsProgramScreenAction } from '../../../store/programscreen/programscreen.actions';
import {
    initAndResetBiaxialVerticalStrip,
    setHorizontalOptionsProgramScreen,
} from '../../../store/programscreen/programscreen.reducers';
import { Mousetrap, ProgressBar } from '../../common';
import { ContentScreenPresentationId } from '../ContentScreenPresentationId';
import { ImageItem, TextItem } from './BHLItems';
import { BiaxialActionWidgetHelper } from './BiaxialActionWidgetHelper';
import { BiaxialHorizontalList } from './BiaxialHorizontalList';
import ContentPreviewComponent from './ContentPreviewComponent';
import {
    THorizontalActionStripImageItem,
    THorizontalActionStripItem,
    THorizontalActionStripLayout,
    THorizontalActionStripTextItem,
} from './THorizontalActionStripItem';

import './BiaxialActionWidget.scss';

interface BiaxialActionWidgetProps {
    actions: Detail[];
    optionsToDisplay: string[];
    collectionId: string;
    contentId: string;
    initialVerticalHighlightIndex: number;
    initialHorizontalHighlightIndex: number;
    contentScreenType: string;
    partnerId?: string;
    collapsedVodItemId?: string;
    contentSupplierPartnerId?: string;
}

const TAG: string = 'BiaxialActionWidget';

const BiaxialActionWidget: FunctionComponent<BiaxialActionWidgetProps> = ({
    actions,
    optionsToDisplay,
    collectionId,
    initialVerticalHighlightIndex,
    initialHorizontalHighlightIndex,
    contentId,
    contentScreenType,
    partnerId,
    collapsedVodItemId,
    contentSupplierPartnerId,
}): JSX.Element => {
    const dispatch = useTivoDispatch();

    // global state listeners
    const currentVerticalOptionIndex = useTivoSelector(
        (state: RootState) => state.programScreen.currentVerticalHighlightIndex,
    );
    const currentHighlightIndex = useTivoSelector(
        (state: RootState) => state.programScreen.currentHighlightIndex,
    );
    const _horizontalOptions = useTivoSelector(
        (state: RootState) => state.programScreen.horizontalOptions,
    );
    const viewAllDir = useTivoSelector((state: RootState) => state.programScreen.viewAllDirection);

    // screen local state
    const [verticalOptions, setVerticalOptions] = useState<React.ReactNode[]>([]);
    const [selectedHorizontalIndex, setSelectedHorizontalIndex] = useState<number>(
        initialHorizontalHighlightIndex,
    );
    const [layoutType, setLayoutType] = useState<THorizontalActionStripLayout>('text');
    const [componentType, setComponentType] = useState<string>('');
    const [isDataAvailable, setIsDataAvailable] = useState<boolean>(true);

    // screen refs
    const currentPresentationId = useRef<string>('');
    const isFirstCall = useRef<boolean>(true);

    const actionList = (actions[currentVerticalOptionIndex] as any)?.actions;
    const previewKernel =
        actionList &&
        actionList[currentHighlightIndex] &&
        actionList[currentHighlightIndex]?.previewKernel;

    const services = new NdvrInteractor();
    const newIcon = services.getAppUiImageByKey(BrandingUiInfoKeys.APP_ICONS.NEW_ICON);
    const liveIcon = services.getAppUiImageByKey(BrandingUiInfoKeys.APP_ICONS.LIVE_ICON);

    useEffect(() => {
        setIsDataAvailable(true);
        const fetchItems = async (presentationId: string) => {
            const items = await generateHorizontalItems();
            // checking for the current presentationId to avoid data overlapping
            if (currentPresentationId.current === presentationId) {
                const horizontalOptionsSharedPreferenceEditor = new SharedPreferences(
                    SharedPrefConstants.PROGRAM_SCREEN_PREF_KEY,
                ).edit();
                horizontalOptionsSharedPreferenceEditor.putJson(
                    SharedPrefConstants.PROGRAM_SCREEN_HORIZONTAL_OPTIONS,
                    items as unknown as JSON,
                );
                horizontalOptionsSharedPreferenceEditor.apply();
                dispatch(setHorizontalOptionsProgramScreen(items));
                setSelectedHorizontalIndex(0);
                setVerticalOptions(renderItems(optionsToDisplay));
            }
        };
        const currentDetails = actions[currentVerticalOptionIndex];
        if (currentDetails) {
            const presentationId = currentDetails.presentation?.presentationId;
            // setting current presentationId to avoid data overlapping
            currentPresentationId.current = presentationId || '';
            fetchItems(presentationId || '');
        }
        return () => {
            setComponentType('');
            BiaxialActionWidgetHelper.reset();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentVerticalOptionIndex, JSON.stringify(actions)]);

    useEffect(() => {
        isFirstCall.current = true;
        dispatch(
            initAndResetBiaxialVerticalStrip(
                initialVerticalHighlightIndex,
                optionsToDisplay.length,
            ),
        );
        return () => {
            dispatch(initAndResetBiaxialVerticalStrip(0, 0));
            dispatch(setHorizontalOptionsProgramScreenAction([]));
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [actions.length]);

    const renderItems = (optionList) => {
        return optionList.map((option) => (
            <div key={option} className="horizontal-list-item centre">
                {option}
            </div>
        ));
    };

    const generateImageLayout = (item: THorizontalActionStripImageItem, highlighted: boolean) => {
        return (
            <React.Fragment>
                {highlighted && viewAllDir !== 'none' ? (
                    <div className="biaxial-widget-view-all-container">
                        {viewAllDir === 'left' && <span className="view-all-arrow left"></span>}
                        <span className="biaxial-widget-view-all-text-container">
                            {Locale.getString(LocaleKeys.VIEW_ALL_TEXT).toLowerCase()}
                        </span>
                        {viewAllDir === 'right' && <span className="view-all-arrow right"></span>}
                    </div>
                ) : null}
                <div className={`biaxial-widget-image-container`}>
                    {item.displayDetails.season ? (
                        <div className="biaxial-widget-image-text-section">
                            {item.displayDetails.season === -1
                                ? Locale.getString(LocaleKeys.EXTRA)
                                : `${Locale.getString(LocaleKeys.SEASON)} ${item.displayDetails.season} `}
                        </div>
                    ) : null}
                    {item.displayDetails.identifier === 'view-all' ? (
                        <div className="biaxial-widget-image-text-section view-all">
                            {item.displayDetails.name.toUpperCase()}
                        </div>
                    ) : null}
                    <ImageItem
                        layout={item.displayDetails.layout}
                        key={item.displayDetails.name}
                        image={item.displayDetails.image || ''}
                        type={item.type}
                        value={item.displayDetails.identifier}
                        title={item.displayDetails.title}
                        isHighlighted={highlighted}
                    />
                    {item.displayDetails.identifier !== 'view-all' && (
                        <div className="overlay-read-text"></div>
                    )}
                    {highlighted ? <div className="highlighted highlighted-tile"></div> : null}
                    {item.displayDetails?.contentType !== 'movie' ? (
                        <>
                            {item.displayDetails.originalAirDate &&
                            !item.displayDetails.seasonNumber ? (
                                <span className="biaxial-widget-image-season-number-section">{`${item.displayDetails.originalAirDate}`}</span>
                            ) : null}
                            {item.displayDetails.episode && highlighted ? (
                                <div className="biaxial-widget-image-episode-section">
                                    {`${item.displayDetails.episode} ${Locale.getString(LocaleKeys.EPISODE)} `}
                                </div>
                            ) : null}
                            {item.displayDetails.seasonNumber ? (
                                <span className="biaxial-widget-image-season-number-section">
                                    {`S${item.displayDetails.seasonNumber}${
                                        item.displayDetails.episodeNumber
                                            ? ` E${item.displayDetails.episodeNumber}`
                                            : ''
                                    }`}
                                </span>
                            ) : null}
                            <div className="biaxial-widget-icons-container">
                                {item.displayDetails.isLive &&
                                item.displayDetails.identifier !== 'view-all' ? (
                                    <div>
                                        <img src={liveIcon?.imageUrl} alt="Live Icon" />
                                    </div>
                                ) : null}

                                {item.displayDetails.isNew &&
                                item.displayDetails.identifier !== 'view-all' ? (
                                    <div>
                                        <img src={newIcon?.imageUrl} alt="New Icon" />
                                    </div>
                                ) : null}
                            </div>
                        </>
                    ) : null}

                    {item.displayDetails.channelUrl && highlighted ? (
                        <span className="biaxial-widget-channel-logo">
                            <img
                                src={item.displayDetails.channelUrl}
                                className="biaxial-widget-channel-logo"
                                onError={(e) => {
                                    const img = e.target as HTMLImageElement;
                                    img.style.display = 'none';
                                }}
                            ></img>
                        </span>
                    ) : null}
                    {item.displayDetails.identifier === 'view-all' ? (
                        <span className="view-all-border"></span>
                    ) : null}
                </div>
            </React.Fragment>
        );
    };

    function doesResumeFromMyShowsActionExist(): boolean {
        return actionList.some(
            (action) =>
                action.kernel?.actionKernelType === ActionKernelType.ResumeFromMyShowsActionKernel,
        );
    }

    const generateTextLayout = (item: THorizontalActionStripTextItem, highlighted: boolean) => {
        const { providerId, channelUrl, displayDetails } = item;
        let highlightName;
        let defaultName;
        let defaultContent;
        let highlightedContent;
        if (displayDetails.name) {
            highlightedContent = (
                <div className="text-item-container">
                    <div className="text-content">{displayDetails.name}</div>
                </div>
            );
            return (
                <TextItem
                    defaultContent={
                        <div className="default-text-container">{displayDetails.name}</div>
                    }
                    highlighted={highlighted}
                    highlightedContent={highlightedContent}
                    key={displayDetails.name}
                />
            );
        }
        const { actionKernelType, actionId } = displayDetails?.action?.kernel || {};
        switch (actionKernelType) {
            case ActionKernelType.WatchFromVodActionKernel:
            case ActionKernelType.WatchFromVodOfferActionKernel:
            case ActionKernelType.WatchFromSocuActionKernel: {
                const isProviderIconOnly = !highlighted && providerId;
                const getSocuIconUrl = new ServicesClientImpl().generateProviderImageUrl(
                    providerId,
                    49,
                    49,
                );
                if (isProviderIconOnly) {
                    defaultContent = (
                        <div className="provider-icon-only-container">
                            <div className="provider-logo-socu">
                                <img src={getSocuIconUrl} />
                            </div>
                        </div>
                    );
                } else {
                    highlightName =
                        item.identifier?.provider?.displayName ||
                        Locale.getString(LocaleKeys[`PS_OPTION_${actionId}`]) ||
                        actionId ||
                        Locale.getString(LocaleKeys.PS_OPTION_UNKNOWN);
                    defaultName =
                        item.identifier?.provider?.displayName ||
                        Locale.getString(LocaleKeys[`PS_OPTION_${actionId}`]) ||
                        actionId ||
                        Locale.getString(LocaleKeys.PS_OPTION_UNKNOWN);
                    defaultContent = (
                        <div className="text-item-container text-only-container">
                            <div className="text-content">{defaultName}</div>
                        </div>
                    );
                    highlightedContent = (
                        <div className="text-item-container">
                            {providerId && (
                                <div className="provider-logo-socu">
                                    <img src={getSocuIconUrl} />
                                </div>
                            )}
                            <div className="text-content">{highlightName}</div>
                            {highlighted && displayDetails.action.percentWatched > 0 && (
                                <span className="text-item-progress-bar">
                                    <ProgressBar
                                        percentage={displayDetails.action.percentWatched}
                                    />
                                </span>
                            )}
                        </div>
                    );
                }
                return (
                    <TextItem
                        defaultContent={defaultContent}
                        highlighted={highlighted}
                        highlightedContent={highlightedContent}
                        key={highlightName}
                    />
                );
            }
            case ActionKernelType.WatchFromMyShowsActionKernel:
            case ActionKernelType.WatchLiveActionKernel:
            case ActionKernelType.ResumeFromMyShowsActionKernel: {
                /*
                Logic : If the action API response contains only WatchFromMyShowsActionKernel 
                then this action is mapped to "Play" string
                But if both WatchFromMyShowsActionKernel and ResumeFromMyShowsActionKernel exists , 
                WatchFromMyShowsActionKernel is mapped to "Start Over" string while 
                ResumeFromMyShowsActionKernel is mapped to "Play" string
                */
                let buttonTitle = '';
                if (actionKernelType === ActionKernelType.ResumeFromMyShowsActionKernel) {
                    buttonTitle = Locale.getString(LocaleKeys.PS_OPTION_PLAY);
                } else if (actionKernelType === ActionKernelType.WatchFromMyShowsActionKernel) {
                    buttonTitle = doesResumeFromMyShowsActionExist()
                        ? Locale.getString(LocaleKeys.PS_OPTION_START_OVER)
                        : Locale.getString(LocaleKeys.PS_OPTION_PLAY);
                }
                highlightName =
                    item.identifier?.provider?.displayName ||
                    buttonTitle ||
                    Locale.getString(LocaleKeys[`PS_OPTION_${actionId}`]) ||
                    actionId ||
                    Locale.getString(LocaleKeys.PS_OPTION_UNKNOWN);
                defaultName = highlightName;
                if (!highlighted && channelUrl) {
                    defaultContent = (
                        <div className="text-item-container text-only-container">
                            <div className="text-content">{defaultName}</div>
                        </div>
                    );
                    return (
                        <TextItem
                            defaultContent={defaultContent}
                            highlighted={highlighted}
                            key={highlightName}
                        />
                    );
                }
                highlightedContent = (
                    <div className="text-item-container">
                        <div className="text-content">{highlightName}</div>
                        {highlighted && (
                            <React.Fragment>
                                {displayDetails.action.isPpv && <div className="ppv-logo"></div>}
                                {displayDetails.action.videoResolution === 'hd' &&
                                    !displayDetails.action.isPpv && <div className="hd-logo"></div>}
                                {displayDetails.action.videoResolution === 'sd' &&
                                    !displayDetails.action.isPpv && <div className="sd-logo"></div>}
                                {displayDetails.action.isNew && (
                                    <div className="new-label">
                                        <img src={newIcon?.imageUrl} />
                                    </div>
                                )}
                                {channelUrl && (
                                    <div className="provider-logo">
                                        <img
                                            src={channelUrl}
                                            onError={(e) => {
                                                const img = e.target as HTMLImageElement;
                                                img.style.display = 'none';
                                            }}
                                        />
                                    </div>
                                )}
                                {displayDetails.action.percentWatched > 0 && (
                                    <span className="text-item-progress-bar">
                                        <ProgressBar
                                            percentage={displayDetails.action.percentWatched}
                                        />
                                    </span>
                                )}
                            </React.Fragment>
                        )}
                    </div>
                );
                return (
                    <TextItem
                        defaultContent={<div className="default-text-container">{defaultName}</div>}
                        highlighted={highlighted}
                        highlightedContent={highlightedContent}
                        key={highlightName}
                    />
                );
            }
            case ActionKernelType.DeleteRecordingActionKernel: {
                highlightName =
                    displayDetails.action?.provider?.displayName ||
                    Locale.getString(LocaleKeys[`PS_OPTION_${actionId}`]) ||
                    actionId ||
                    Locale.getString(LocaleKeys.PS_OPTION_UNKNOWN);
                defaultName =
                    displayDetails.action?.provider?.displayName ||
                    Locale.getString(LocaleKeys[`PS_OPTION_DEFAULT_${actionId}`]) ||
                    actionId ||
                    Locale.getString(LocaleKeys.PS_OPTION_UNKNOWN);
                break;
            }
            default: {
                highlightName =
                    item.identifier?.provider?.displayName ||
                    Locale.getString(LocaleKeys[`PS_OPTION_${actionId}`]) ||
                    actionId ||
                    Locale.getString(LocaleKeys.PS_OPTION_UNKNOWN);
                defaultName = highlightName;
                break;
            }
        }
        highlightedContent = (
            <div className="text-item-container">
                <div className="text-content">{highlightName}</div>
            </div>
        );
        return (
            <TextItem
                defaultContent={<div className="default-text-container">{defaultName}</div>}
                highlighted={highlighted}
                highlightedContent={highlightedContent}
                key={highlightName}
            />
        );
    };

    const fetchDataFromBiaxialHelper = async (
        id: string,
        layout: Extract<THorizontalActionStripLayout, 'upcoming-airing' | 'all-upcoming-episodes'>,
    ) => {
        services.clearUpcomingOfferCache();

        setComponentType('image');
        setLayoutType(layout);
        const { currentImageIndex } = ScreenController.getInstance()
            .getScreen()
            .getProperties() as any;
        BiaxialActionWidgetHelper.init(id, contentScreenType, layout);

        const res = isFirstCall.current
            ? await BiaxialActionWidgetHelper.fetchData(currentImageIndex, true)
            : await BiaxialActionWidgetHelper.fetchData(0, true);

        isFirstCall.current = false;

        if (!res) return [];
        if (res.totalCount === 0) {
            setIsDataAvailable(false);
        }
        return res.imageInfo;
    };

    const fetchDataFromSeasonsApi = async (
        layout: Extract<THorizontalActionStripLayout, 'all-episodes' | 'on-demand-episodes'>,
    ) => {
        setComponentType('image');
        setLayoutType(layout);
        const service = new NdvrInteractor();
        let res: SeasonsData = {} as SeasonsData;
        try {
            res = await service.getSeasonData({
                unifiedCollectionId: collectionId,
                includeStickyInfo: true,
                filter: layout === 'all-episodes' ? 'all' : 'vodOnDemand',
                sort: 'seasonOrYearAsc',
                partnerId,
                collapsedVodItemId,
                contentSupplierPartnerId,
            });
        } catch (error: any) {
            Log.e(TAG, error.message);
            Diagnostics.error(TAG, error.message);
        }
        const viewAll: THorizontalActionStripImageItem = {
            type: 'PosterImage',
            displayDetails: {
                name: Locale.getString(LocaleKeys.VIEW_ALL_TEXT),
                image: viewAllImage,
                identifier: 'view-all',
                title: 'All episodes',
                layout,
            },
        };
        const emptyStrip: THorizontalActionStripImageItem = {
            type: 'PosterImage',
            displayDetails: {
                name: 'Empty strip',
                image: services.getAppUiImageByKey(BrandingUiInfoKeys.APP_ICONS.DEFAULT_EMPTY_STRIP)
                    ?.imageUrl,
                layout,
                identifier: 'empty-strip',
                title: 'Empty strip',
            },
        };
        const imageUrl = new ImageUrlGenerator();
        const episodeImageUrl = imageUrl.buildImageUrl(
            services.getServicesBaseUrls()['image-host'],
            collectionId,
            'COLLECTION',
        );
        const images: THorizontalActionStripImageItem[] =
            res.seasons?.map((season) => {
                return {
                    type: 'PosterImage',
                    displayDetails: {
                        name: 'All episodes',
                        image: episodeImageUrl,
                        season: season.seasonOrYear,
                        episode: season.episodeCount,
                        identifier: 'seasons',
                        layout,
                    },
                };
            }) || [];
        service.clearSeasonsData();

        return images.length > 0 ? [viewAll, ...images] : [emptyStrip];
    };

    const fetchCastDetails = async (): Promise<THorizontalActionStripImageItem[]> => {
        try {
            const viewAll: THorizontalActionStripImageItem = {
                type: 'LandscapeImage',
                displayDetails: {
                    name: Locale.getString(LocaleKeys.VIEW_ALL_TEXT),
                    image: viewAllImage,
                    identifier: 'view-all',
                    title: '',
                    layout: 'cast',
                },
            };
            const emptyStrip: THorizontalActionStripImageItem = {
                type: 'LandscapeImage',
                displayDetails: {
                    name: 'Empty strip',
                    image: services.getAppUiImageByKey(
                        BrandingUiInfoKeys.APP_ICONS.DEFAULT_EMPTY_STRIP,
                    )?.imageUrl,
                    layout: 'cast',
                    identifier: 'empty-strip',
                    title: 'Empty strip',
                },
            };
            const res = await new VodInteractor().getCollectionCastCredits(
                APPLICATION_FEATURE_AREA.SERIES_SCREEN,
                collectionId,
            );

            // Convert cast data to image items
            const imageService = new ImageAssetsService({
                imageHostUrl: VodInteractor.servicesBaseUrls[TIVO_SERVICES.ImageHost],
            });
            const castItems: THorizontalActionStripImageItem[] = res.map(
                (cast: Credit, index: number) => {
                    const imageUrl = imageService.getPersonImageUrl(cast.personId);
                    return {
                        type: 'LandscapeImage',
                        displayDetails: {
                            name: cast.name,
                            image: imageUrl,
                            identifier: `cast-${index}`,
                            title: cast.name,
                            layout: 'cast',
                        },
                    };
                },
            );

            return castItems.length > 0 ? [viewAll, ...castItems] : [emptyStrip];
        } catch (error: any) {
            // Return empty strip on error
            const emptyStrip: THorizontalActionStripImageItem = {
                type: 'LandscapeImage',
                displayDetails: {
                    name: 'Empty cast',
                    image: services.getAppUiImageByKey(
                        BrandingUiInfoKeys.APP_ICONS.DEFAULT_EMPTY_STRIP,
                    )?.imageUrl,
                    layout: 'cast',
                    identifier: 'empty-cast',
                    title: 'Empty cast',
                },
            };
            return [emptyStrip];
        }
    };

    const generateHorizontalItems = async (): Promise<THorizontalActionStripItem[]> => {
        const currentDetails = actions[currentVerticalOptionIndex];
        const presentationId = currentDetails.presentation?.presentationId;
        const ndvrServices = new NdvrInteractor();
        // based on presentationId, generate image or text items;
        // currently generating a dummy text for all. these will be handled in respective task
        switch (presentationId) {
            case ContentScreenPresentationId.GET_THIS_SHOW_DETAIL:
            case ContentScreenPresentationId.ONE_PASS_OPTIONS_DETAIL:
            case ContentScreenPresentationId.RECORDING_OPTIONS_DETAIL:
                setIsDataAvailable(true);
                setComponentType('text');
                setLayoutType('text');
                return (currentDetails as any).actions?.map((option) => {
                    let textToDisplay = Locale.getString(
                        LocaleKeys[`PS_OPTION_${option?.kernel?.actionId}`],
                    );
                    if (option?.kernel?.year)
                        textToDisplay = textToDisplay.replace('YEAR_NUMBER', option?.kernel?.year);
                    else if (option?.kernel?.seasonNumber) {
                        textToDisplay = textToDisplay.replace(
                            'SEASON_NUMBER',
                            option?.kernel?.seasonNumber,
                        );
                    }
                    return {
                        identifier: option,
                        type: 'text',
                        displayDetails: {
                            action: option,
                            name: textToDisplay,
                        },
                    };
                });
            case ContentScreenPresentationId.WATCH_NOW_DETAIL: {
                setIsDataAvailable(true);
                setComponentType('text');
                setLayoutType('text');
                return (
                    Promise.all(
                        (currentDetails as any).actions?.map(async (option) => {
                            const providerId = option?.provider?.providerId || null;
                            const channelNumber = option?.kernel?.channelNumber;
                            let channelLogoUrl = '';
                            if (
                                option?.kernel?.actionId === 'LIVE_TV' ||
                                option?.kernel?.actionId === 'WATCH_FROM_MYSHOWS'
                            ) {
                                try {
                                    const channelData =
                                        await ndvrServices.getChannelFromChannelNumber(
                                            channelNumber,
                                        );
                                    channelLogoUrl = channelData.channelLogoUrl || '';
                                } catch (error) {
                                    channelLogoUrl = '';
                                }
                            }

                            return {
                                identifier: option,
                                type: 'text',
                                displayDetails: {
                                    action: option,
                                },
                                providerId,
                                channelUrl: channelLogoUrl,
                            };
                        }),
                    ) || [
                        {
                            type: 'text',
                            displayDetails: {
                                name:
                                    currentDetails.detailType ||
                                    Locale.getString(LocaleKeys.PS_OPTION_UNKNOWN_DETAIL),
                            },
                        },
                    ]
                );
            }

            case ContentScreenPresentationId.CAST_DETAIL: {
                setIsDataAvailable(true);
                setComponentType('image');
                setLayoutType('cast');
                return fetchCastDetails();
            }
            case ContentScreenPresentationId.MAY_ALSO_LIKE_DETAIL:
                setIsDataAvailable(true);
                setComponentType('text');
                setLayoutType('text');
                return (
                    (currentDetails as any).actions?.map((option) => {
                        return {
                            type: 'text',
                            displayDetails: {
                                action: option,
                                name: option?.kernel?.actionId,
                            },
                        };
                    }) || [
                        {
                            type: 'text',
                            displayDetails: {
                                name: currentDetails.detailType,
                            },
                        },
                    ]
                );
            case ContentScreenPresentationId.UPCOMING_AIRING_DETAIL:
                return fetchDataFromBiaxialHelper(contentId, 'upcoming-airing');
            case ContentScreenPresentationId.UPCOMING_EPISODES_DETAIL:
                return fetchDataFromBiaxialHelper(collectionId, 'all-upcoming-episodes');
            case ContentScreenPresentationId.SEASON_OR_YEAR_DETAIL:
                return fetchDataFromSeasonsApi('all-episodes');
            case ContentScreenPresentationId.ON_DEMAND_EPISODES_DETAIL:
                return fetchDataFromSeasonsApi('on-demand-episodes');
            case ContentScreenPresentationId.RECOVER_RECORDING_DETAIL:
            case ContentScreenPresentationId.INFO_DETAIL:
                setComponentType('text');
                setLayoutType('text');
                return (currentDetails as any).actions?.map((option) => {
                    return {
                        identifier: option,
                        type: 'text',
                        displayDetails: {
                            action: option,
                            name: `${Locale.getString(
                                LocaleKeys[`PS_OPTION_${option?.kernel?.actionId}`],
                            )}`,
                        },
                    };
                });
            case ContentScreenPresentationId.MORE_INFO_DETAIL: {
                setComponentType('text');
                setLayoutType('text');
                const seeMoreInfoAction = (currentDetails as any).actions?.find(
                    (action) =>
                        action.kernel?.actionKernelType ===
                        ActionKernelType.MoreInfoContentActionKernel,
                );

                if (!seeMoreInfoAction) {
                    Log.e(
                        TAG,
                        `${ActionKernelType.MoreInfoContentActionKernel} actionKernelType not found for presentationType: ${presentationId}`,
                    );
                    return [];
                }

                return [
                    {
                        identifier: seeMoreInfoAction,
                        type: 'text',
                        displayDetails: {
                            action: seeMoreInfoAction,
                            name: Locale.getString(LocaleKeys.PS_OPTION_MORE_INFO_DETAIL),
                        },
                        providerId: '',
                        channelUrl: '',
                    },
                ];
            }
            case ContentScreenPresentationId.WATCH_PREVIEW_DETAIL: {
                setComponentType('text');
                setLayoutType('text');
                const watchPreviewAction = (currentDetails as any).actions?.find((action) =>
                    [
                        ActionKernelType.WatchVodOfferPreviewActionKernel,
                        ActionKernelType.WatchVodContentPreviewActionKernel,
                    ].includes(action.kernel?.actionKernelType),
                );

                if (!watchPreviewAction) {
                    Log.e(
                        TAG,
                        `${ActionKernelType.WatchVodOfferPreviewActionKernel}, ${ActionKernelType.WatchVodContentPreviewActionKernel} actionType not found for presentationType: ${presentationId}`,
                    );
                    return [];
                }

                return [
                    {
                        identifier: watchPreviewAction,
                        type: 'text',
                        displayDetails: {
                            action: watchPreviewAction,
                            name: Locale.getString(LocaleKeys.PS_OPTION_WATCH_PREVIEW_DETAIL),
                        },
                        providerId: '',
                        channelUrl: '',
                    },
                ];
            }

            default:
                Log.e(TAG, `no action available for presentationType: ${presentationId}`);
                return [];
        }
    };

    /**
     * Retrieves the selected preview kernel based on the current state or props.
     *
     * @returns The selected preview kernel.
     */
    const getSelectedPreviewKernel = (): PreviewKernel | TPreviewKernel => {
        if (previewKernel) return previewKernel;

        const optionDetails = _horizontalOptions[currentHighlightIndex]?.displayDetails || {};
        const { layout, offerId } = optionDetails;

        let oPreviewKernel: any = null;

        if (layout === 'all-episodes') {
            oPreviewKernel = {
                previewKernelType: TPreviewKernelType.TAllEpisodesKernel,
                offerId,
            };
        }

        if (layout === 'all-upcoming-episodes') {
            oPreviewKernel = {
                previewKernelType: TPreviewKernelType.TAllUpcomingEpisodesKernel,
                offerId,
            };
        }

        if (layout === 'upcoming-airing') {
            oPreviewKernel = {
                previewKernelType: TPreviewKernelType.TUpcomingAiringsKernel,
                offerId,
            };
        }
        return oPreviewKernel;
    };

    const previewKernelParam = getSelectedPreviewKernel();

    return (
        <div className="biaxial-action-widget-container">
            {/* Vertical Top User Actions Column */}
            <Mousetrap
                key={`top-vertical-action-container`}
                value={'top-vertical-action-container'}
                className={'top-vertical-action-container'}
            >
                {currentVerticalOptionIndex
                    ? verticalOptions.slice(0, currentVerticalOptionIndex)
                    : null}
            </Mousetrap>
            {componentType === 'image' ? (
                <span className="image-component-title">
                    {verticalOptions.slice(
                        currentVerticalOptionIndex,
                        currentVerticalOptionIndex + 1,
                    )}
                </span>
            ) : null}
            {/* Horizontal User Actions Row (BiaxialHorizontalList) */}
            <BiaxialHorizontalList
                key={selectedHorizontalIndex}
                isDataAvailable={isDataAvailable}
                type={componentType}
                items={_horizontalOptions || []}
                renderItem={(
                    item: THorizontalActionStripItem,
                    highlighted: boolean,
                ): React.ReactNode => {
                    if (item.type === 'PosterImage' || item.type === 'LandscapeImage') {
                        return generateImageLayout(item, highlighted);
                    }

                    if (item.type === 'text') {
                        return generateTextLayout(item, highlighted);
                    }

                    return null;
                }}
                initialHighlightIndex={selectedHorizontalIndex}
                contentType={contentScreenType}
                layoutType={layoutType}
            />

            {/* Vertical Bottom User Actions Column */}
            <Mousetrap
                key={`bottom-vertical-action-container`}
                value={'bottom-vertical-action-container'}
                className={'bottom-vertical-action-container'}
            >
                {verticalOptions.slice(currentVerticalOptionIndex + 1)}
            </Mousetrap>

            <div className="content-preview-component">
                {previewKernelParam ? (
                    <ContentPreviewComponent previewKernel={previewKernelParam} />
                ) : null}
            </div>
        </div>
    );
};

export default BiaxialActionWidget;
